export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0)
}

export function formatDate(value: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

export function formatDateTime(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

// Returns a human relative string like "2 months ago", "66 days ago".
export function formatRelative(value: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000)
  if (days < 0) return 'in the future'
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 45) return `${days} days ago`
  const months = Math.round(days / 30)
  if (months < 18) return `${months} month${months === 1 ? '' : 's'} ago`
  const years = Math.round(days / 365)
  return `${years} year${years === 1 ? '' : 's'} ago`
}

// Formats a unit cost: 2 decimals, or up to 4 when 2 would hide the price
// (e.g. $0.0333 per oz from "$10 for 300 oz").
export function formatUnitCost(value: number): string {
  const twoDp = value.toFixed(2)
  if (Math.abs(Number(twoDp) - value) < 1e-9) return `$${twoDp}`
  return `$${Number(value.toFixed(4)).toString()}`
}

// Bigger units to switch to once an amount reaches a whole one of them.
// Keys are the unit names in Settings > Units (lowercased).
const STEP_UP: Record<string, { unit: string; per: number }[]> = {
  mg: [{ unit: 'g', per: 1000 }, { unit: 'kg', per: 1_000_000 }],
  g: [{ unit: 'kg', per: 1000 }],
  ml: [{ unit: 'l', per: 1000 }],
  tsp: [{ unit: 'tbsp', per: 3 }, { unit: 'cup', per: 48 }],
  tbsp: [{ unit: 'cup', per: 16 }],
  'fl oz': [{ unit: 'qt', per: 32 }, { unit: 'gal', per: 128 }],
  cup: [{ unit: 'qt', per: 4 }, { unit: 'gal', per: 16 }],
  pt: [{ unit: 'qt', per: 2 }, { unit: 'gal', per: 8 }],
  qt: [{ unit: 'gal', per: 4 }],
}

function roundAmount(n: number): string {
  const digits = Math.abs(n) >= 100 ? 0 : Math.abs(n) >= 10 ? 1 : 2
  return n.toLocaleString('en-US', { maximumFractionDigits: digits })
}

/**
 * Formats an ingredient amount for people: large amounts move to a bigger
 * unit (1,472.84 oz → "92 lb 1 oz", 2,500 g → "2.5 kg", 160 fl oz →
 * "1.25 gal") and big numbers drop extra decimals.
 */
export function formatAmount(amount: number, unit: string | null): string {
  const key = (unit ?? '').trim().toLowerCase()
  if (key === 'oz' && amount >= 16) {
    let lb = Math.floor(amount / 16)
    let oz = Math.round(amount - lb * 16)
    if (oz === 16) {
      lb += 1
      oz = 0
    }
    return oz > 0 ? `${lb.toLocaleString('en-US')} lb ${oz} oz` : `${lb.toLocaleString('en-US')} lb`
  }
  const step = [...(STEP_UP[key] ?? [])].reverse().find(s => amount >= s.per)
  if (step) return `${roundAmount(amount / step.per)} ${step.unit}`
  return `${roundAmount(amount)} ${unit ?? ''}`.trim()
}
